import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import { authService } from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';

const UserLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    emailOrPhone: location?.state?.email || '',
    password: '',
    rememberMe: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(location?.state?.message || '');
  const [loginSuccess, setLoginSuccess] = useState(false);

  // Redirect if already logged in OR after successful login
  useEffect(() => {
    if (user) {
      navigate('/user-dashboard', { replace: true });
    }
  }, [user, navigate]);

  // Clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.emailOrPhone?.trim()) {
      newErrors.emailOrPhone = 'Email o número de teléfono es requerido';
    } else {
      const isEmail = formData?.emailOrPhone?.includes('@');
      const isPhone = /^[\d\s+()-]+$/?.test(formData?.emailOrPhone);
      
      if (!isEmail && !isPhone) {
        newErrors.emailOrPhone = 'Ingrese un email o número de teléfono válido';
      }
    }

    if (!formData?.password) {
      newErrors.password = 'La contraseña es requerida';
    } else if (formData?.password?.length < 6) {
      newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setLoginSuccess(false);
    
    try {
      // Use email for login (phone login would require additional setup)
      const email = formData?.emailOrPhone?.includes('@') 
        ? formData?.emailOrPhone 
        : `${formData?.emailOrPhone}@bizznet.temp`; // Fallback for phone
      
      const { data, error } = await authService?.signIn(email, formData?.password);
      
      if (error) {
        throw error;
      }
      
      // Mark login as successful and stop loading
      setLoginSuccess(true);
      setIsLoading(false);
      
      // AuthContext will update user state, triggering navigation via useEffect
      
    } catch (error) {
      setErrors({ 
        password: error?.message || 'Credenciales inválidas. Por favor verifica tu email y contraseña.' 
      });
      setIsLoading(false);
      setLoginSuccess(false);
    }
  };

  const handleSocialLogin = (provider) => {
    // TODO: Implement social login
    console.log(`Login with ${provider}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="mb-8 text-center">
          <Link 
            to="/login-selection" 
            className="inline-flex items-center gap-2 text-gray-600 hover:text-emerald-600 transition-colors mb-6"
          >
            <Icon name="ArrowLeft" size={20} />
            <span>Volver a selección</span>
          </Link>
          
          <div className="w-20 h-20 bg-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Icon name="User" size={40} color="#FFFFFF" />
          </div>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Bienvenido de vuelta
          </h1>
          <p className="text-gray-600">
            Ingresa tus credenciales para acceder a tu cuenta
          </p>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 bg-green-50 border-2 border-green-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Icon name="CheckCircle" size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-green-900 font-medium">{successMessage}</p>
            </div>
          </div>
        )}

        {/* Login Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email or Phone Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email o Número de Teléfono
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Icon name="Mail" size={20} className="text-gray-400" />
                </div>
                <input
                  type="text"
                  name="emailOrPhone"
                  value={formData?.emailOrPhone}
                  onChange={handleChange}
                  placeholder="ejemplo@correo.com o +57 312 456 7890"
                  className={`w-full pl-10 pr-4 py-3 border ${
                    errors?.emailOrPhone ? 'border-red-500' : 'border-gray-300'
                  } rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500`}
                />
              </div>
              {errors?.emailOrPhone && (
                <p className="mt-1 text-sm text-red-600">{errors?.emailOrPhone}</p>
              )}
            </div>

            {/* Password Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contraseña
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Icon name="Lock" size={20} className="text-gray-400" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData?.password}
                  onChange={handleChange}
                  placeholder="Ingresa tu contraseña"
                  className={`w-full pl-10 pr-12 py-3 border ${
                    errors?.password ? 'border-red-500' : 'border-gray-300'
                  } rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  <Icon 
                    name={showPassword ? 'EyeOff' : 'Eye'} 
                    size={20} 
                    className="text-gray-400 hover:text-gray-600"
                  />
                </button>
              </div>
              {errors?.password && (
                <p className="mt-1 text-sm text-red-600">{errors?.password}</p>
              )}
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="flex items-center justify-between">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="rememberMe"
                  checked={formData?.rememberMe}
                  onChange={handleChange}
                  className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                />
                <span className="ml-2 text-sm text-gray-600">Recordar mis datos</span>
              </label>
              
              <Link 
                to="/password-recovery-request" 
                state={{ accountType: 'user' }}
                className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
              >
                ¿Olvidaste tu contraseña?
              </Link>
            </div>

            {/* Login Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <Icon name="Loader2" size={20} className="animate-spin" />
                  <span>Iniciando sesión...</span>
                </>
              ) : (
                <>
                  <span>Iniciar Sesión</span>
                  <Icon name="ArrowRight" size={20} />
                </>
              )}
            </button>
          </form>

          {/* Register Link */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              ¿No tienes una cuenta?{' '}
              <Link to="/user-registration" className="text-emerald-600 hover:text-emerald-700 font-medium">
                Regístrate aquí
              </Link>
            </p>
          </div>

          {/* Security Badge */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
              <Icon name="Shield" size={16} className="text-emerald-600" />
              <span>Conexión segura SSL</span>
            </div>
          </div>
        </div>

        {/* Privacy Policy */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            Al iniciar sesión, aceptas nuestros{' '}
            <Link to="/terms" className="text-emerald-600 hover:underline">
              Términos y Condiciones
            </Link>
            {' '}y{' '}
            <Link to="/privacy" className="text-emerald-600 hover:underline">
              Política de Privacidad
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default UserLogin;